// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useState } from 'react';
import {
  Alert, Box, Button, Chip, Divider, Grid, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import api from '../../utils/api';
import { HOUSEHOLD_STATUSES } from './vocabulaire';

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <Box sx={{ mb: 0.75 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

/**
 * Onglet « Informations » de la fiche d'un foyer : contacts, personne suivie,
 * abonnement et, pour le support, le parc de caméras et sa facturation.
 *
 * `includedDraft` (saisie en cours du nombre de caméras incluses) est tenu par
 * la page : le rechargement qui suit chaque geste démonte l'onglet, et la
 * saisie y survit.
 */
export default function InfoTab({
  clientId, client, comptesActifs: activeAccounts, cameraInfo, onCameraInfo, includedDraft, onIncludedDraft,
  onSnack, onErreur: onError, onRecharger: onReload,
}) {
  const [camSaving, setCamSaving] = useState(false);
  const isIndividual = client.client_type === 'individual';

  // Caméras offertes et resynchronisation Stripe. `resync: true` par défaut côté
  // backend : offrir une caméra doit retirer le supplément déjà facturé, sinon
  // le geste commercial ne se voit que dans notre base.
  const saveCameraBilling = async (payload, label) => {
    setCamSaving(true);
    try {
      const res = await api.put(`/clients/${clientId}/camera-billing`, payload);
      onCameraInfo(info => (info ? { ...info, billing: res.data.billing } : info));
      onIncludedDraft(null);   // la valeur serveur reprend la main
      const manual = res.data.billing?.manual;
      onSnack(manual
        ? `${label} enregistré, mais Stripe n'a pas suivi (${manual}) — à régulariser`
        : `${label} appliqué`);
      onReload();
    } catch (e) {
      onError(e.response?.data?.detail || `Erreur : ${label}`);
    } finally {
      setCamSaving(false);
    }
  };

  const markOrder = async (orderId, status) => {
    setCamSaving(true);
    try {
      await api.put(`/clients/${clientId}/camera-orders/${orderId}`, { status });
      onSnack('Commande mise à jour');
      onReload();
    } catch (e) {
      onError(e.response?.data?.detail || 'Erreur commande caméra');
    } finally {
      setCamSaving(false);
    }
  };

  return (
    <Grid container spacing={3}>
      {isIndividual ? (
        <>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Contact payeur</Typography>
              <Divider sx={{ mb: 1.5 }} />
              <InfoRow label="Nom" value={[client.contact_first_name, client.contact_last_name].filter(Boolean).join(' ')} />
              <InfoRow label="Téléphone" value={client.contact_phone} />
              <InfoRow label="Email" value={client.contact_email} />
              <InfoRow label="Adresse" value={[client.contact_address, client.contact_city, client.contact_postal_code].filter(Boolean).join(', ')} />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Personne suivie</Typography>
              <Divider sx={{ mb: 1.5 }} />
              <InfoRow label="Nom" value={[client.monitored_first_name, client.monitored_last_name].filter(Boolean).join(' ')} />
              <InfoRow label="Date de naissance" value={client.monitored_dob ? new Date(client.monitored_dob).toLocaleDateString('fr-FR') : null} />
              <InfoRow label="Adresse" value={[client.monitored_address, client.monitored_city, client.monitored_postal_code].filter(Boolean).join(', ')} />
            </Paper>
          </Grid>
          {client.emergency_contacts?.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Contacts d'urgence</Typography>
                <Divider sx={{ mb: 1.5 }} />
                {client.emergency_contacts.map((ec, i) => (
                  <Box key={i} sx={{ mb: 1 }}>
                    <Typography variant="body2"><strong>{ec.name}</strong> · {ec.relationship} · {ec.phone}</Typography>
                  </Box>
                ))}
              </Paper>
            </Grid>
          )}
        </>
      ) : (
        <>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Établissement</Typography>
              <Divider sx={{ mb: 1.5 }} />
              <InfoRow label="Raison sociale" value={client.company_name} />
              <InfoRow label="SIRET" value={client.siret} />
              <InfoRow label="Adresse" value={[client.company_address, client.company_city, client.company_postal_code].filter(Boolean).join(', ')} />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Contact</Typography>
              <Divider sx={{ mb: 1.5 }} />
              <InfoRow label="Nom" value={[client.company_contact_name, client.company_contact_role ? `(${client.company_contact_role})` : ''].filter(Boolean).join(' ')} />
              <InfoRow label="Téléphone" value={client.company_phone} />
              <InfoRow label="Email" value={client.company_email} />
              <InfoRow label="Début contrat" value={client.contract_start ? new Date(client.contract_start).toLocaleDateString('fr-FR') : null} />
            </Paper>
          </Grid>
        </>
      )}

      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Abonnement & accès app</Typography>
          <Divider sx={{ mb: 1.5 }} />
          <InfoRow label="Formule" value={client.plan_type} />
          <InfoRow label="Prix mensuel" value={client.monthly_price ? `${client.monthly_price} €/mois` : null} />
          {/* L'app se connecte avec l'email d'un compte famille ;
              `clients.app_username` ne sert pas. */}
          <InfoRow label="Accès app" value={activeAccounts.map(u => u.email).join(', ')} />
          <InfoRow label="Statut" value={HOUSEHOLD_STATUSES[client.status]?.label} />
          <InfoRow label="Notes" value={client.notes} />
        </Paper>
      </Grid>

      {/* Caméras et facturation. L'écart entre le parc réel et ce que
          Stripe facture ne se voit nulle part ailleurs : caméra en service
          non facturée (synchronisation tombée) ou l'inverse (caméra
          retirée hors application). */}
      {cameraInfo && (
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
              <Typography variant="subtitle2">Caméras & facturation</Typography>
              <Box sx={{ flex: 1 }} />
              <Button
                size="small"
                startIcon={<RestartAltIcon />}
                disabled={camSaving}
                onClick={() => saveCameraBilling({ resync: true }, 'Resynchronisation Stripe')}
              >
                Resynchroniser
              </Button>
            </Box>
            <Divider sx={{ mb: 1.5 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <InfoRow label="En service (facturables)" value={String(cameraInfo.billing?.billable_cameras ?? '—')} />
                <InfoRow label="Incluses (offertes)" value={String(cameraInfo.billing?.included_cameras ?? '—')} />
                <InfoRow label="Suppléments dus" value={String(cameraInfo.billing?.extra_cameras ?? '—')} />
                <InfoRow
                  label="Facturées chez Stripe"
                  value={cameraInfo.billing?.billed_cameras == null ? 'jamais synchronisé' : String(cameraInfo.billing.billed_cameras)}
                />
                <InfoRow
                  label="Tarif attendu"
                  value={cameraInfo.billing?.expected_monthly_price != null ? `${cameraInfo.billing.expected_monthly_price} €/mois` : null}
                />
                {/* Caméras attendues : celle de base et les supplémentaires.
                    Un écart signale une régularisation en attente. */}
                {cameraInfo.billing
                  && cameraInfo.billing.billed_cameras != null
                  && cameraInfo.billing.billed_cameras !== 1 + (cameraInfo.billing.extra_cameras || 0) && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Stripe facture {cameraInfo.billing.billed_cameras} caméra(s) pour&nbsp;
                    {1 + (cameraInfo.billing.extra_cameras || 0)} attendue(s) — resynchronisez.
                  </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    type="number"
                    label="Caméras incluses"
                    value={includedDraft ?? String(cameraInfo.billing?.included_cameras ?? 1)}
                    inputProps={{ min: 1, max: cameraInfo.billing?.max_cameras ?? 10 }}
                    onChange={(e) => onIncludedDraft(e.target.value)}
                    sx={{ width: 160 }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={camSaving}
                    onClick={() => saveCameraBilling(
                      {
                        cameras_included: Number(includedDraft ?? cameraInfo.billing?.included_cameras ?? 1),
                        resync: true,
                      },
                      'Caméras incluses',
                    )}
                  >
                    Appliquer
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={7}>
                {cameraInfo.cameras?.length ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Caméra</TableCell>
                        <TableCell>Kit</TableCell>
                        <TableCell>Statut</TableCell>
                        <TableCell>En service depuis</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cameraInfo.cameras.map(cam => (
                        <TableRow key={cam.id}>
                          <TableCell>{cam.label || cam.mac}</TableCell>
                          <TableCell>{cam.kit_code || '—'}</TableCell>
                          <TableCell>{cam.status}</TableCell>
                          <TableCell>
                            {cam.activated_at
                              ? new Date(cam.activated_at).toLocaleDateString('fr-FR')
                              : 'jamais (non facturée)'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="text.secondary">Aucune caméra rattachée.</Typography>
                )}

                {cameraInfo.orders?.filter(o => o.status !== 'cancelled').length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Commandes de caméra</Typography>
                    <Table size="small">
                      <TableBody>
                        {cameraInfo.orders.filter(o => o.status !== 'cancelled').map(o => (
                          <TableRow key={o.id}>
                            <TableCell>{new Date(o.created_at).toLocaleDateString('fr-FR')}</TableCell>
                            <TableCell>{o.quantity} caméra(s)</TableCell>
                            <TableCell>
                              {[o.shipping?.name, o.shipping?.address, o.shipping?.postal_code, o.shipping?.city]
                                .filter(Boolean).join(', ')}
                            </TableCell>
                            <TableCell>
                              <Chip size="small"
                                color={o.status === 'awaiting_payment' ? 'warning' : 'default'}
                                label={{
                                  awaiting_payment: 'Paiement en attente',
                                  requested: 'Payée, à expédier',
                                  shipped: 'Expédiée',
                                  delivered: 'Livrée',
                                }[o.status] || o.status} />
                            </TableCell>
                            <TableCell align="right">
                              {o.status === 'requested' && (
                                <Button size="small" disabled={camSaving} onClick={() => markOrder(o.id, 'shipped')}>
                                  Expédiée
                                </Button>
                              )}
                              {o.status === 'shipped' && (
                                <Button size="small" disabled={camSaving} onClick={() => markOrder(o.id, 'delivered')}>
                                  Livrée
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
}
